import { ApolloClient, ApolloError, ApolloQueryResult, FetchResult, MutationOptions, QueryOptions } from '@apollo/client'
import { TooManyPollAttemptsError } from './polling'
import * as TE from 'fp-ts/es6/TaskEither'
import { pipe } from 'fp-ts/es6/pipeable'

export function handleNetworkError(networkError: unknown): ApolloError {
  return networkError instanceof ApolloError
    ? networkError
    : new ApolloError({
        networkError: networkError as Error,
      })
}

export function hasGraphQlErrors<T>(result: ApolloQueryResult<T> | FetchResult<T>): boolean {
  return result?.errors?.length !== undefined && result?.errors?.length > 0
}

export function mapTooManyRetriesError(e: ApolloError | TooManyPollAttemptsError): ApolloError {
  if (e instanceof TooManyPollAttemptsError && e.__typename === 'TooManyPollAttemptsError') {
    return new ApolloError({
      networkError: e,
    })
  } else {
    return e as ApolloError
  }
}

export function doMutate<M, V>(apolloClient: ApolloClient<unknown>, options: MutationOptions<M, V>): TE.TaskEither<ApolloError, FetchResult<M>> {
  return pipe(
    TE.tryCatch(() => apolloClient.mutate<M, V>(options), handleNetworkError),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    TE.chain((result: FetchResult<M>) => {
      if (!hasGraphQlErrors(result)) {
        return TE.right(result)
      } else {
        const extraInfo =
          options.errorPolicy === 'all'
            ? {
                partiallyCompleteResult: result,
              }
            : undefined
        return TE.left<ApolloError, M>(
          new ApolloError({
            graphQLErrors: result.errors,
            extraInfo,
          }),
        )
      }
    }),
  )
}

export function doQuery<Q, V>(apolloClient: ApolloClient<unknown>, options: QueryOptions<V>): TE.TaskEither<ApolloError, ApolloQueryResult<Q>> {
  return pipe(
    TE.tryCatch(() => {
      return apolloClient.query<Q, V>(options)
    }, handleNetworkError),
    TE.mapLeft((e) => {
      return e
    }),
    TE.chain((result: ApolloQueryResult<Q>) => {
      if (!hasGraphQlErrors(result)) {
        return TE.right(result)
      } else {
        const extraInfo =
          options.errorPolicy === 'all'
            ? {
                partiallyCompleteResult: result,
              }
            : undefined
        return TE.left<ApolloError, ApolloQueryResult<Q>>(
          new ApolloError({
            graphQLErrors: result.errors,
            extraInfo,
          }),
        )
      }
    }),
  )
}
